import { Pipe, PipeTransform } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { CONSTANTS } from '@pk/powerkioskutils';

import _ from 'lodash';

import { Agent } from '../models';
import { LicensePlan } from '../models/license-plan';

@Pipe({
	name: 'licensePlanControlFilter',
	standalone: false,
})
export class LicensePlanControlFilterPipe implements PipeTransform {
	private businessFullIds = CONSTANTS.licensePlans.businessFullIds;
	private oldBusinessFullIds = CONSTANTS.licensePlans.oldBusinessFullIds;
	private newSalesCodeBundleIds = CONSTANTS.licensePlans.salesCodeBundleIds;
	private oldSalesCodeBundleIds = CONSTANTS.licensePlans.oldSalesCodeBundleIds;
	private salesCodeBundleIds = this.newSalesCodeBundleIds.concat(this.oldSalesCodeBundleIds);
	private newAgentBundleIds = CONSTANTS.licensePlans.newAgentBundleIds;
	private oldAgentBundleIds = CONSTANTS.licensePlans.oldAgentBundleIds;
	private agentBundleIds = this.newAgentBundleIds.concat(this.oldAgentBundleIds);
	private newTurnKeyIds = CONSTANTS.licensePlans.newTurnKeyIds;
	private oldTurnKeyIds = CONSTANTS.licensePlans.oldTurnKeyIds;
	private turnKeyIds = this.newTurnKeyIds.concat(this.oldTurnKeyIds);
	private newApiIds = CONSTANTS.licensePlans.newApiIds;
	private oldApiIds = CONSTANTS.licensePlans.oldApiIds;
	private apiIds = this.newApiIds.concat(this.oldApiIds);
	private newAffiliateIds = CONSTANTS.licensePlans.newAffiliateIds;
	private affiliateIds = this.newAffiliateIds;
	private billReaderAIIds = CONSTANTS.licensePlans.billReaderAIIds;
	private leadIds = CONSTANTS.licensePlans.leadIds;

	// only show the main new ones
	// all other should be considered separate prices
	private showAgentBundle(licensePlan: LicensePlan, agent: Agent) {
		const licensePlanId = licensePlan.id;
		return (
			!this.agentBundleIds.includes(licensePlanId) || // not a agent bundle, return it
			(!agent.parentId &&
				((this.newAgentBundleIds.includes(licensePlanId) && (agent.hasNoAgentBundle() || agent.hasDefaultAgentBundle())) || // only new if none or new
					(this.oldAgentBundleIds.includes(licensePlanId) && agent.hasSeparateAgentBundle())))
		);
	}

	// only show the main new ones
	// all other should be considered separate prices
	private showSalesCodeBundle(licensePlan: LicensePlan, agent: Agent) {
		const licensePlanId = licensePlan.id;
		return (
			!this.salesCodeBundleIds.includes(licensePlanId) || // not a sales code bundle, return it
			(this.newSalesCodeBundleIds.includes(licensePlanId) && (agent.hasNoSalesCodeBundle() || agent.hasDefaultSalesCodeBundle())) || // only new if none or new
			(this.oldSalesCodeBundleIds.includes(licensePlanId) && agent.hasSeparateSalesCodeBundle())
		);
	}

	// only show the main new ones
	// all other should be considered separate prices
	private showTurnKey(licensePlan: LicensePlan, agent: Agent) {
		const licensePlanId = licensePlan.id;
		return (
			!this.turnKeyIds.includes(licensePlanId) || // not a turn key, return it
			(this.newTurnKeyIds.includes(licensePlanId) && (agent.hasNoTurnKey() || agent.hasDefaultTurnKey())) || // only new if none or new
			(this.oldTurnKeyIds.includes(licensePlanId) && agent.hasSeparateTurnKey())
		);
	}

	// only show the main new ones
	// all other should be considered separate prices
	private showAPI(licensePlan: LicensePlan, agent: Agent) {
		const licensePlanId = licensePlan.id;
		return (
			!this.apiIds.includes(licensePlanId) || // not a api, return it
			(this.newApiIds.includes(licensePlanId) && (agent.hasNoAPI() || agent.hasDefaultAPI())) || // only new if none or new
			(this.oldApiIds.includes(licensePlanId) && agent.hasSeparateAPI())
		);
	}

	// only show to admin
	private showAffiliatePlan(licensePlan: LicensePlan) {
		const licensePlanId = licensePlan.id;
		return (
			!this.affiliateIds.includes(licensePlanId) || // not a affiliate plan, return it
			false
		); // false because only admin can view, isAdmin already at top level of logic in transform()
	}

	// only show to admin
	private showBillReaderAI(licensePlan: LicensePlan) {
		const licensePlanId = licensePlan.id;
		return (
			!this.billReaderAIIds.includes(licensePlanId) || // not a bill reader ai plan, return it
			false
		); // false because only admin can view, isAdmin already at top level of logic in transform()
	}

	// only show on parent
	private showAllLeads(licensePlan: LicensePlan, agent: Agent) {
		const licensePlanId = licensePlan.id;
		return (
			!this.leadIds.includes(licensePlanId) || // not a leads plan, return it
			!agent.parentId
		);
	}

	// do not show unless already assigned it
	private showParentBusinessLicenses(licensePlan: LicensePlan, agent: Agent, isAdmin: boolean) {
		const licensePlanId = licensePlan.id;
		return (
			this.businessFullIds.includes(licensePlanId) || // return business full
			(isAdmin && this.oldBusinessFullIds.includes(licensePlanId)) || // return special business fulls
			(CONSTANTS.licensePlans.businessLiteIds.includes(licensePlanId) && agent.hasSeparateBusinessLite())
		);
	}

	// admin should be able to assign all parent licenses
	private isAdminAndParentLicenses(agent: Agent, isAdmin: boolean) {
		return isAdmin && !agent.parentId;
	}

	// admin should be able to assign all $0 licenses for sub-agents
	// except for the affiliate licenses
	private isAdminAndSubAgentLicenses(licensePlan: LicensePlan, agent: Agent, isAdmin: boolean) {
		const licensePlanId = licensePlan.id;
		return (
			isAdmin &&
			agent.parentId &&
			!licensePlan.monthlyFee &&
			!licensePlan.upfrontFee &&
			!this.affiliateIds.includes(licensePlanId) &&
			!this.billReaderAIIds.includes(licensePlanId)
		);
	}

	transform(licensePlans: AbstractControl[], parentId: number, agent: Agent, isAdmin: boolean): any {
		if (!licensePlans) {
			return licensePlans;
		}

		return _.orderBy(
			parentId
				? licensePlans.filter(
						l =>
							l.value.entity.parents.some(p => p.id === parentId) &&
							(this.isAdminAndParentLicenses(agent, isAdmin) ||
								this.isAdminAndSubAgentLicenses(l.value.entity, agent, isAdmin) ||
								(this.showAllLeads(l.value.entity, agent) &&
									this.showAgentBundle(l.value.entity, agent) &&
									this.showSalesCodeBundle(l.value.entity, agent) &&
									this.showTurnKey(l.value.entity, agent) &&
									this.showAPI(l.value.entity, agent) &&
									this.showAffiliatePlan(l.value.entity) &&
								this.showBillReaderAI(l.value.entity))),
					)
				: licensePlans.filter(l => !l.value.entity.parents?.length && this.showParentBusinessLicenses(l.value.entity, agent, isAdmin)),
			l => l.value.entity.name,
			'desc',
		);
	}
}
